import React, { useState, useMemo, useEffect } from "react";
import { Search, Package, RefreshCw, X, Edit, Check } from "lucide-react";
import { useAuth } from "../../context/authContext";
import { useNavigate } from "react-router-dom";
import {toast} from 'react-hot-toast';
import Loader from "../loader";
const API = process.env.REACT_APP_API_URL;

const ORDER_STATUS_UI = {
  placed: { title: "Order placed", subtitle: "We’ve received your order", color: "text-yellow-600", canTrack: false },
  packed: { title: "Order packed", subtitle: "Your items are packed", color: "text-blue-600", canTrack: false },
  shipped: { title: "Order shipped", subtitle: "Your order is on the way", color: "text-indigo-600", canTrack: true },
  "out for delivery": { title: "Out for delivery", subtitle: "Your order will arrive today", color: "text-green-600", canTrack: true },
  delivered: { title: "Delivered", subtitle: "Your order has been delivered", color: "text-green-700", canTrack: false },
  cancelled: { title: "Cancelled", subtitle: "This order was cancelled", color: "text-red-600", canTrack: false },
};
const PAYMENT_UI = {
  paid: {
    label: "Paid",
    color: "text-green-600 bg-green-50 border-green-200"
  },
  created: {
    label: "Cash on Delivery",
    color: "text-orange-600 bg-orange-50 border-orange-200"
  },
  refund_pending: {
    label: "Refund Initiated",
    color: "text-blue-600 bg-blue-50 border-blue-200"
  }
};



const STATUS_FLOW = ["placed", "packed", "shipped", "out for delivery", "delivered"];


const OrdersPage = () => {
  const { orders ,fetchOrders } = useAuth();
  const navigate = useNavigate();

  const [searchText, setSearchText] = useState("");
  const [filterTime, setFilterTime] = useState("past 3 months");

  const [userAddresses, setUserAddresses] = useState([]);
  const [isFetchingAddresses, setIsFetchingAddresses] = useState(false);

  const [changeAddressModal, setChangeAddressModal] = useState({ open: false, orderId: null });
  const [selectedTempAddress, setSelectedTempAddress] = useState(null);

  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);

  const [addressForm, setAddressForm] = useState({
    name: "",
    phone_number: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    zip: "",
    type: "Home",
  });

  const [cancelModal, setCancelModal] = useState({ open: false, orderId: null });
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(true);

  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let y = currentYear; y >= currentYear - 10; y--) yearOptions.push(y.toString());

  const token = localStorage.getItem("token") || "";

  useEffect(() => {
  const loadOrders = async () => {
    setLoadingOrders(true);
    await fetchOrders();
    setLoadingOrders(false);
  };

  loadOrders();
}, []);

  // Fetch addresses when modal opens
  useEffect(() => {
    if (changeAddressModal.open && token) {
      const fetchAddresses = async () => {
        setIsFetchingAddresses(true);
        try {
          const res = await fetch(`${API}/api/user/addresses`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.ok) {
            const data = await res.json();
            setUserAddresses(data || []);
          }
        } catch (err) {
          console.error("Failed to fetch addresses:", err);
        } finally {
          setIsFetchingAddresses(false);
        }
      };
      fetchAddresses();
    } else {
      setUserAddresses([]);
    }
  }, [changeAddressModal.open, token]);

  // Auto-select current order's address when addresses load
  useEffect(() => {
    if (userAddresses.length > 0 && changeAddressModal.open) {
      const currentOrder = orders.find((o) => o.orderId === changeAddressModal.orderId);
      const currentAddrId = currentOrder?.address_id;

      if (currentAddrId) {
        const matching = userAddresses.find((a) => a.id === currentAddrId);
        if (matching) {
          setSelectedTempAddress(matching);
        }
      }
    }
  }, [userAddresses, changeAddressModal.open, orders, changeAddressModal.orderId]);

  const resetAddressForm = () => {
    setAddressForm({
      name: "",
      phone_number: "",
      line1: "",
      line2: "",
      city: "",
      state: "",
      zip: "",
      type: "Home",
    });
    setIsEditingAddress(false);
    setEditingAddressId(null);
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddressForm((prev) => ({ ...prev, [name]: value }));
  };

  const isFormValid =
    addressForm.name.trim() &&
    addressForm.phone_number.trim() &&
    addressForm.line1.trim() &&
    addressForm.city.trim() &&
    addressForm.state.trim() &&
    addressForm.zip.trim();

  const handleSaveAddress = async () => {
    if (!isFormValid) {
      toast.error("Please fill all required fields (name, phone_number, line1, city, state, zip)");
      return;
    }

    if (!editingAddressId) {
      toast.error("No address selected for editing");
      return;
    }

    const payload = {
      id: editingAddressId,
      name: addressForm.name.trim(),
      phone_number: addressForm.phone_number.trim(),
      line1: addressForm.line1.trim(),
      line2: addressForm.line2.trim() || null,
      city: addressForm.city.trim(),
      state: addressForm.state.trim(),
      zip: addressForm.zip.trim(),
      type: addressForm.type.trim(),
    };

    try {
      // 1. Update user_addresses table
      const updateUserRes = await fetch(`${API}/api/user/addresses/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!updateUserRes.ok) {
        const errData = await updateUserRes.json().catch(() => ({}));
        throw new Error(errData.message || "Failed to update user address");
      }

      // 2. Update order snapshot (now using correct field names)
      const updateOrderRes = await fetch(`${API}/api/orders/${changeAddressModal.orderId}/address`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!updateOrderRes.ok) {
        const errData = await updateOrderRes.json().catch(() => ({}));
        console.error("Order address update failed:", errData);
        throw new Error(errData.message || "Failed to update order address");
      }

      toast.success("Address updated successfully");
      resetAddressForm();
      setChangeAddressModal({ open: false, orderId: null });
      window.location.reload();
    } catch (err) {
      console.error("Update error:", err);
      toast.error(err.message || "Failed to update address");
    }
  };

  const handleChangeOrderAddress = async (newAddress) => {
    try {
      const payload = {
        addressId: newAddress.id,
        name: newAddress.name?.trim() || "",
        phone_number: (newAddress.phone_number || newAddress.phone || "").trim(),
        line1: newAddress.line1?.trim() || "",
        line2: newAddress.line2?.trim() || null,
        city: newAddress.city?.trim() || "",
        state: newAddress.state?.trim() || "",
        zip: (newAddress.zip || newAddress.zipcode || "").trim(),
        type: newAddress.type?.trim() || "Home",
      };

      // Safety check – ensure required fields
      if (!payload.name || !payload.phone_number || !payload.line1 || !payload.city || !payload.state || !payload.zip) {
        throw new Error("Selected address is missing required fields");
      }

      const res = await fetch(`${API}/api/orders/${changeAddressModal.orderId}/address`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        toast.success("Order address changed successfully");
        setChangeAddressModal({ open: false, orderId: null });
        window.location.reload();
      } else {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || "Failed to change order address");
      }
    } catch (err) {
      console.error("Change address error:", err);
      toast.error(err.message || "Error changing order address");
    }
  };

  const uniqueOrders = useMemo(() => {
  const map = new Map();
  orders.forEach(order => {
    map.set(order.orderId, order);
  });
  return Array.from(map.values());
}, [orders]);
  const filteredOrders = useMemo(() => {
    const now = new Date();
    
    return uniqueOrders.filter((order) => {
      let orderDate;
      try {
        const [day, month, year] = order.date.split("/");
        orderDate = new Date(Number(year), Number(month) - 1, Number(day));
      } catch {
        orderDate = new Date(order.date);
      }
      if (isNaN(orderDate)) return false;

      let timeMatch = false;
      if (filterTime === "past 30 days") {
        const past30 = new Date(now);
        past30.setDate(now.getDate() - 30);
        timeMatch = orderDate >= past30;
      } else if (filterTime === "past 3 months") {
        const past3 = new Date(now);
        past3.setMonth(now.getMonth() - 3);
        timeMatch = orderDate >= past3;
      } else if (filterTime.length === 4) {
        timeMatch = orderDate.getFullYear().toString() === filterTime;
      }

      const searchMatch =
        order.orderId.toLowerCase().includes(searchText.toLowerCase()) ||
        order.items.some((item) => item.name.toLowerCase().includes(searchText.toLowerCase()));

      return timeMatch && searchMatch;
    });
  }, [uniqueOrders, searchText, filterTime]);

  const [expandedOrders, setExpandedOrders] = useState([]);

const toggleExpand = (orderId) => {
  setExpandedOrders((prev) =>
    prev.includes(orderId)
      ? prev.filter((id) => id !== orderId)
      : [...prev, orderId]
  );
};

if (loadingOrders) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white gap-4">
      {/* <Loader />  */}
      <p className="text-red-600 text-lg animate-pulse">Fetching orders...</p>
    </div>
  );
}
  if (!orders || orders.length === 0) {
    return (
      <div className="max-w-7xl mx-auto p-10 text-center">
        <h2 className="text-2xl font-bold">You have no orders yet.</h2>
        <p className="text-gray-600">Start shopping now!</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen text-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-8">
          <h1 className="text-3xl font-semibold">My Orders</h1>
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input
              type="text"
              placeholder="Search all orders"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
            />
          </div>
        </div>

        {/* Filter */}
        <div className="flex items-center gap-3 mb-8 text-sm">
          <span className="font-medium">{filteredOrders.length} orders</span>
          <span>placed in</span>
          <select
            value={filterTime}
            onChange={(e) => setFilterTime(e.target.value)}
            className="bg-white border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 shadow-sm"
          >
            <option value="past 30 days">Past 30 days</option>
            <option value="past 3 months">Past 3 months</option>
            {yearOptions.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        {/* Orders List */}
        <div className="space-y-4">
          {filteredOrders.length === 0 ? (
            <div className="text-center text-gray-500 py-12">No orders found matching your criteria.</div>
          ) : (
            filteredOrders.map((order) => {
              const statusUI = ORDER_STATUS_UI[order.status] || {};
              const canEdit = ["placed", "packed"].includes(order.status);
              const currentAddress = order.address || {};
              // const displayedItems = order.items.slice(0, 3);
              // const extraItemsCount = order.items.length > 3 ? order.items.length - 3 : 0;
               const displayedItems = expandedOrders.includes(order.orderId)
    ? order.items
    : order.items.slice(0, 3);

  const extraItemsCount = expandedOrders.includes(order.orderId)
    ? 0
    : order.items.length > 3
      ? order.items.length - 3
      : 0;

              return (
                <div key={order.orderId} className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 flex flex-col md:flex-row gap-4">
                  {/* Item Thumbnails */}
                  <div className="flex flex-wrap items-center gap-2">
                    {displayedItems.map((item, idx) => (
  <div key={idx} className="flex flex-col items-center w-16">
    <img
      src={`${API}${item.img}`}
      alt={item.name}
      className="w-16 h-16 object-contain border rounded-md bg-gray-50"
    />
    <p className="text-xs text-center text-black mt-2 w-15">{item.name}</p>
    <p className="text-xs text-gray-500 sm:-mt-2 -mt-5 ">Qty: {item.qty}</p>
  </div>
))}
                    {extraItemsCount > 0 && (
  <button
    onClick={() => toggleExpand(order.orderId)}
    className="w-16 h-16 bg-gray-100 border rounded-md flex items-center justify-center text-sm font-medium text-gray-600 hover:bg-gray-200"
  >
    +{extraItemsCount}
  </button>
)}
                  </div>

                  {/* Order Details */}
                  <div className="flex-grow">
  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3 lg:grid-cols-4 lg:gap-4 text-sm mb-2 sm:mt-0 -mt-10">
    <div>
      <div className="text-xs text-gray-500 uppercase">Order ID</div>
      <div className="font-medium">{order.orderId}</div>
    </div>
    <div>
      <div className="text-xs text-gray-500 uppercase">Order Placed</div>
      <div className="font-medium">{order.date}</div>
    </div>
    <div>
      <div className="text-xs text-gray-500 uppercase">Status</div>
      <div className={`font-medium ${statusUI.color || "text-gray-800"}`}>
        {statusUI.title || order.status}
      </div>
    </div>
    <div>
      <div className="text-xs text-gray-500 uppercase">Total</div>
      <div className="font-medium">₹{order.total}</div>
    </div>
    <div>
      <div className="text-xs text-gray-500 uppercase font-medium">
        Phone NO: {currentAddress.phone_number}
      </div>
    </div>
  </div>

  <div className="text-sm text-gray-600 mb-2">
    <div className="text-xs text-gray-500 uppercase mb-1">Delivery Address</div>
    <div>
      {currentAddress.name || "—"}, {currentAddress.line1 || ""}, {currentAddress.line2 || ""} {currentAddress.city || "—"}, {currentAddress.state || "—"}
    </div>
  </div>

  <div className="text-sm text-gray-600">
    <div className="text-xs text-gray-500 uppercase mb-1">Payment</div>
    {order.paymentStatus === "paid" ? (
      <span className="text-green-600 font-medium">Payment Done • {order.paymentMode.toUpperCase()}</span>
    ) : order.paymentMode === "cod" ? (
      <span className="text-orange-600 font-medium">Cash on Delivery</span>
    ) : (
      <span className="text-gray-600 font-medium">Payment Pending</span>
    )}
  </div>
</div>

                  {/* Actions and Timeline */}
                  <div className="flex-shrink-0 flex flex-col gap-2">
                    <div className="flex flex-wrap gap-2">
                      {statusUI.canTrack && (
  <button
    className="border border-gray-300 px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors"
    onClick={() => toast.error("Tracking not available yet")}
  >
    Track
  </button>
)}
                      {canEdit && (
                        <>
                          <button
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm transition-colors"
                            onClick={() => setCancelModal({ open: true, orderId: order.orderId })}
                          >
                            Cancel
                          </button>
                          <button
                            className="border border-gray-300 px-3 py-1 rounded text-sm hover:bg-red-500 transition-colors"
                            onClick={() => setChangeAddressModal({ open: true, orderId: order.orderId })}
                          >
                            Change Address
                          </button>
                        </>
                      )}
                      {order.status === "cancelled" && (
                        <button
                          className="bg-gray-400 text-white px-3 py-1 rounded text-sm cursor-not-allowed"
                          disabled
                        >
                          Cancelled
                        </button>
                      )}
                    </div>
                    {/* Compact Timeline */}
                    <div className="flex gap-1 text-xs">
                      {STATUS_FLOW.map((step, index) => {
                        const isActive = STATUS_FLOW.indexOf(step) <= STATUS_FLOW.indexOf(order.status);
                        return (
                          <div key={step} className="flex items-center">
                            <div
                              className={`w-2 h-2 rounded-full ${isActive ? "bg-blue-600" : "bg-gray-300"}`}
                            ></div>
                            {index < STATUS_FLOW.length - 1 && (
                              <div
                                className={`w-4 h-0.5 ${isActive ? "bg-blue-600" : "bg-gray-300"}`}
                              ></div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="text-xs text-gray-500 text-center">
                      {statusUI.subtitle || ""}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

        {/* Change Address Modal */}
        {changeAddressModal.open && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-gray-50 to-white">
                <h2 className="text-xl font-semibold text-gray-900">Change Delivery Address</h2>
                <button
                  onClick={() => setChangeAddressModal({ open: false, orderId: null })}
                  className="p-2 rounded-full hover:bg-gray-200 transition-colors"
                >
                  <X size={20} className="text-gray-600" />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 overflow-y-auto flex-1 bg-gray-50">
                {isFetchingAddresses ? (
                  <div className="flex flex-col items-center justify-center py-16">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                    <p className="mt-4 text-gray-600">Loading your saved addresses...</p>
                  </div>
                ) : userAddresses.length === 0 ? (
                  <div className="text-center py-16 text-gray-600">
                    <p className="text-xl font-medium">No saved addresses</p>
                    <p className="mt-2">Add an address from your profile to use it here.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {userAddresses.map((addr) => {
                      const currentOrder = orders.find((o) => o.orderId === changeAddressModal.orderId);
                      const isCurrent = addr.id === currentOrder?.address_id;
                      const isSelected = selectedTempAddress?.id === addr.id;
                      const isEditingThis = isEditingAddress && editingAddressId === addr.id;

                      return (
                        <div
                          key={addr.id}
                          className={`
                            relative p-6 border rounded-2xl transition-all duration-200 cursor-pointer group
                            ${isSelected
                              ? "border-2 border-blue-500 bg-blue-50/70 shadow-lg ring-1 ring-blue-300"
                              : "border border-gray-200 hover:border-gray-300 hover:shadow-xl bg-white"}
                          `}
                          onClick={() => setSelectedTempAddress(addr)}
                        >
                          {/* Checkmark indicator */}
                          {(isSelected) && (
                            <div className="absolute -top-3 -right-3">
                              <div className="bg-blue-600 text-white rounded-full p-2 shadow-md">
                                <Check size={20} />
                              </div>
                            </div>
                          )}

                          {/* Address Details */}
                          <div className="pr-16">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="font-bold text-gray-900 text-lg">{addr.name || "—"}</span>
                              <span className="text-sm text-gray-500">•</span>
                              <span className="text-sm text-gray-700 font-medium">{addr.phone_number || addr.phone || "—"}</span>
                            </div>

                            <div className="text-sm text-gray-700 leading-relaxed space-y-1.5">
                              {addr.type && (
                                <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium">
                                  {addr.type}
                                </span>
                              )}
                              <p className="font-medium  text-gray-700">{addr.line1}{addr.line2 ? `, ${addr.line2}` : ""}</p>
                              <p className="font-medium  text-gray-700">{addr.city}, {addr.state} – {addr.zip || addr.zipcode || "—"}</p>
                            </div>
                          </div>

                          {/* Edit button – always visible if current/selected */}
                          {(isSelected) && (
                            <div className="absolute top-7 right-1 md:top-6 md:right-6 ">
                              <button
  onClick={(e) => {
    e.stopPropagation();
    setIsEditingAddress(true);
    setEditingAddressId(addr.id);
    setAddressForm({
      name: addr.name || "",
      phone_number: addr.phone_number || addr.phone || "",
      line1: addr.line1 || "",
      line2: addr.line2 || "",
      city: addr.city || "",
      state: addr.state || "",
      zip: addr.zip || addr.zipcode || "",
      type: addr.type || "Home",
    });
  }}
  style={{
    backgroundColor: "transparent",
    border: "none",
    boxShadow: "none",
    marginLeft: "auto", 
    outline: "none",
  }}
  className="
    p-2 md:p-3 text-blue-700
    hover:text-blue-800 transition-colors
  "
  title="Edit this address"
>
  <Edit size={16} className="md:w-5 md:h-5" />
</button>

                            </div>
                          )}

                          {/* Edit button for others – hover only */}
                          {!(isSelected) && (
                            <div className="
                              absolute top-4 right-4 md:top-6 md:right-6 opacity-0 group-hover:opacity-100
                              transition-opacity duration-200
                            ">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setIsEditingAddress(true);
                                  setEditingAddressId(addr.id);
                                  setAddressForm({
                                    name: addr.name || "",
                                    phone_number: addr.phone_number || addr.phone || "",
                                    line1: addr.line1 || "",
                                    line2: addr.line2 || "",
                                    city: addr.city || "",
                                    state: addr.state || "",
                                    zip: addr.zip || addr.zipcode || "",
                                    type: addr.type || "Home",
                                  });
                                }}
                                className="
                                  p-2 md:p-3 text-gray-700
                                  hover:text-gray-800 transition-colors
                                "
                                title="Edit this address"
                              >
                                <Edit size={16} className="md:w-5 md:h-5" />
                              </button>
                            </div>
                          )}

                          {isEditingThis && (
                            <div className="mt-6 pt-6 border-t border-gray-200 bg-white rounded-b-2xl -mx-6 -mb-6 px-6 pb-6">
                              <div className="text-lg font-semibold text-gray-800 mb-5">Edit Address</div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <input name="name" placeholder="Full Name *" value={addressForm.name} onChange={handleAddressChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none shadow-sm" />
                                <input name="phone_number" placeholder="Phone Number *" value={addressForm.phone_number} onChange={handleAddressChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none shadow-sm" />
                                <select name="type" value={addressForm.type} onChange={handleAddressChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none shadow-sm">
                                  <option value="Home">Home</option>
                                  <option value="Work">Work</option>
                                  <option value="Other">Other</option>
                                </select>
                                <input name="line1" placeholder="Flat, House no., Building *" value={addressForm.line1} onChange={handleAddressChange} className="md:col-span-2 w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none shadow-sm" />
                                <input name="line2" placeholder="Area, Street, Sector (optional)" value={addressForm.line2} onChange={handleAddressChange} className="md:col-span-2 w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none shadow-sm" />
                                <input name="city" placeholder="City *" value={addressForm.city} onChange={handleAddressChange} className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none shadow-sm" />
                                <input name="state" placeholder="State *" value={addressForm.state} onChange={handleAddressChange} className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none shadow-sm" />
                                <input name="zip" placeholder="PIN Code *" value={addressForm.zip} onChange={handleAddressChange} className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none shadow-sm" />
                              </div>

                              <div className="mt-8 flex justify-end gap-4">
                              
                                <button
                                  onClick={resetAddressForm}
                                  className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={handleSaveAddress}
                                  disabled={!isFormValid}
                                  className={`px-8 py-3 rounded-xl text-white font-medium transition-all duration-200 shadow-md ${
                                    isFormValid 
                                      ? "bg-blue-600 hover:bg-blue-700 hover:shadow-lg" 
                                      : "bg-gray-400 cursor-not-allowed"
                                  }`}
                                >
                                  Save Changes
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer */}
              {/* Footer */}
<div className="px-6 py-5 border-t bg-gray-50 flex flex-col sm:flex-row justify-end gap-4">
  <button
    onClick={() => setChangeAddressModal({ open: false, orderId: null })}
    className="
      h-12 sm:h-11
      px-6
      flex items-center justify-center
      border border-gray-300 rounded-xl
      font-medium text-gray-700
      hover:bg-red-50 transition-colors
      text-sm sm:text-base
    "
  >
    Close
  </button>

  <button
    onClick={() => {
      if (selectedTempAddress) {
        handleChangeOrderAddress(selectedTempAddress);
      }
    }}
    disabled={!selectedTempAddress}
    className={`
      h-12 sm:h-11
      px-8
      flex items-center justify-center
      rounded-xl
      font-medium text-white
      transition-all duration-200
      shadow-md
      text-sm sm:text-base
      ${
        selectedTempAddress
          ? "bg-blue-600 hover:bg-blue-700 hover:shadow-lg"
          : "bg-gray-400 cursor-not-allowed"
      }
    `}
  >
    Confirm Selected Address
  </button>
</div>

            </div>
          </div>
        )}

        {/* Cancel Modal */}
        {cancelModal.open && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
              <h2 className="text-xl font-semibold mb-4 text-gray-900">Cancel Order</h2>
              <p className="mb-6 text-gray-700">
                Are you sure you want to cancel order <strong>{cancelModal.orderId}</strong>?
              </p>
              <div className="flex justify-end gap-4">
                <button
                  className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-medium text-gray-700"
                  onClick={() => setCancelModal({ open: false, orderId: null })}
                >
                  No
                </button>
                <button
                  className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium shadow-md disabled:opacity-50"
                  disabled={loadingCancel}
                  onClick={async () => {
                    setLoadingCancel(true);
                    try {
                      const res = await fetch(`${API}/api/payment/cancel/${cancelModal.orderId}`, {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                          Authorization: `Bearer ${token}`,
                        },
                      });
                      const data = await res.json();
                      toast.success(data.message || "Order cancelled");
                      setCancelModal({ open: false, orderId: null });
                      await fetchOrders();
                    } catch (err) {
                      toast.error("Failed to cancel order");
                    } finally {
                      setLoadingCancel(false);
                    }
                  }}
                >
                  {loadingCancel ? "Cancelling..." : "Yes, Cancel"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
  );
};

export default OrdersPage;